import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Grid,
  IconButton,
  FormControlLabel,
  Switch,
  InputAdornment,
  Typography,
  Dialog as MuiDialog, DialogActions, DialogContent as MuiDialogContent, DialogTitle as MuiDialogTitle, List, ListItem, Checkbox, Button as MuiButton, Typography as MuiTypography, Avatar,
  FormHelperText,
  CircularProgress
} from "@material-ui/core";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { eventCategories } from "../../../../Components/Utilities/ListUtils";
import { useDispatch } from "react-redux";
import { getAllEvents, getMyEvents, getSingleEvent, postEvent } from "../../../../Redux/Reducers/communityReducer";
import { useSelector } from "react-redux";
import { Autocomplete } from "@mui/lab";
import { getGoogleLocalPlaces } from "../../../../Redux/helpers/JobsApi";
import currencyCodes from "currency-codes";
import getSymbolFromCurrency from "currency-symbol-map";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import BurstModeIcon from '@mui/icons-material/BurstMode';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import { useQuantityContext } from "../../../QuantityContext";
import "./BookedEvent.css";
import { useProfileContext } from "Components/ProfileDetailContext";
import { getGlobleUsers } from "../../../../Redux/Reducers/ContactReducer";
import debounce from "lodash/debounce";
import { useUserDetailContext } from "../../../../Components/UserDetailContext";

const CreateEventModal = ({ open, onClose, editMode = false, eventData = {}, onSubmit }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const exploreType = useSelector((state) => state?.community?.exploreType);
  const { userDetails } = useUserDetailContext();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: exploreType,
    event_category: "",
    event_access_level: "Public",
    mode: "Offline",
    event_location: "",
    start_time: null,
    end_time: null,
    attendees_limit: "",
    is_paid: false,
    price_amount: "",
    price_currency: "",
    company_name: "",
    feature_image: null,
    media: [],
  });
  const [existingFeatureImage, setExistingFeatureImage] = useState(null);
  const [existingMedia, setExistingMedia] = useState([]);
  const ProfessionalExperience =
    useSelector((state) => state?.ProfileArray?.experienceData?.data) || [];
  const startDate = formData.start_date ? new Date(formData.start_date) : null;
  const startTime = formData.start_time ? new Date(formData.start_time) : null;
  const endDate = formData.end_date ? new Date(formData.end_date) : null;
  const endTime = formData.end_time ? new Date(formData.end_time) : null;
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const allUsers = useSelector(state => state.ContactArray.ConnectedContact) || [];
  const globalusersData = useSelector(state => state.ContactArray?.globalusersData?.data) || [];
  const [errors, setErrors] = useState({});
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hover, setHover] = useState(false);
  const { openSnackbar } = useQuantityContext();
  // New state for search
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchValue) => {
      const params = {
        searchParam: searchValue.trim() === "" ? "a" : searchValue,
        limit: 20,
        offsetValue: 1,
      };
      await dispatch(getGlobleUsers(params));
      setIsSearching(false);
    }, 500),
    [dispatch]
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const isVideo = (file) => {
    if (typeof file === 'string') {
      return ['.mp4', '.mov', '.avi', '.mkv', '.webm'].some(ext =>
        file.toLowerCase().endsWith(ext)
      );
    }
    return file?.type?.startsWith('video/');
  };

  useEffect(() => {
    if (editMode && eventData) {
      setFormData({
        ...formData,
        ...eventData,
        company_name: eventData?.organizer?.name || "",
        start_time: eventData?.start_time ? dayjs(eventData?.start_time) : null,
        end_time: eventData?.end_time ? dayjs(eventData?.end_time) : null,
        feature_image: null,
        media: [],
      });
      setExistingFeatureImage(eventData?.feature_image);
      setExistingMedia(eventData?.media || []);
    } else if (!editMode) {
      setExistingFeatureImage(null);
      setExistingMedia([]);
    }
    // eslint-disable-next-line
  }, [editMode]);

  useEffect(() => {
    if (!editMode || !existingMedia || existingMedia?.length === 0) return;

    const generateThumbnailsForExistingVideos = async () => {
      for (const mediaObj of existingMedia) {
        const url = mediaObj.url || mediaObj;
        if (isVideo(mediaObj)) {
          if (!videoThumbnails[url]) {
            try {
              const response = await fetch(url);
              const blob = await response.blob();
              const file = new File([blob], "existing-video", { type: blob.type });
              await generateVideoThumbnail(file, url);
            } catch (error) {
              console.error("Error generating thumbnail for existing video:", error);
            }
          }
        }
      }
    };

    generateThumbnailsForExistingVideos();
  }, [existingMedia, editMode]);

  const verifiedCompanies = React.useMemo(() => {
    const uniqueCompanies = new Set();
    ProfessionalExperience.forEach((exp) => {
      if (exp?.is_verified && exp?.company_name) {
        uniqueCompanies.add(exp?.company_name);
      }
    });
    return Array.from(uniqueCompanies);
  }, [ProfessionalExperience]);

  const handleFileChange = (field, isMultiple = false) => async (e) => {
    const files = Array.from(e.target.files);

    if (field === "feature_image") {
      if (files[0] && files[0].type.startsWith('image/')) {
        setFormData({ ...formData, feature_image: files[0] });
      } else {
        setErrors(prev => ({
          ...prev,
          feature_image: "Only image files are allowed for featured image"
        }));
      }
    }
    else if (field === "media") {
      if (formData.media?.length + files?.length <= 10) {
        const newFiles = [...files];
        setFormData({ ...formData, media: [...formData.media, ...newFiles] });

        for (const file of newFiles) {
          if (file.type.startsWith('video/')) {
            await generateVideoThumbnail(file);
          }
        }
      } else {
        alert("You can upload a maximum of 10 gallery files.");
      }
    }
  };

  const handleRemoveImage = (field, index = null) => {
    if (field === "feature_image") {
      setFormData({ ...formData, feature_image: null });
    } else if (field === "media") {
      const updatedMedia = formData?.media?.filter((_, i) => i !== index);
      setFormData({ ...formData, media: updatedMedia });
    }
  };

  const handleModeChange = (e) => {
    const mode = e.target.value;
    setFormData({
      ...formData,
      mode,
      ...(mode === "Online" ? { event_location: "" } : { event_link: "" }),
    });
  };

  const handleAccessLevelChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, event_access_level: value });
  };

  const toggleInviteUser = (contactRefId) => {
    setInvitedUsers(prev =>
      prev.includes(contactRefId)
        ? prev.filter(id => id !== contactRefId)
        : [...prev, contactRefId]
    );
  };

  const generateVideoThumbnail = (file, customId = null) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const identifier = customId || file.name;

      const source = customId ? customId : URL.createObjectURL(file);
      video.src = source;
      video.crossOrigin = 'anonymous';

      video.onloadeddata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        video.currentTime = video.duration * 0.25;
      };

      video.onseeked = () => {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          const thumbnailUrl = URL.createObjectURL(blob);
          setVideoThumbnails(prev => ({
            ...prev,
            [identifier]: thumbnailUrl
          }));
          resolve(thumbnailUrl);
        }, 'image/jpeg');
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    const now = dayjs();

    if (!formData.feature_image && !existingFeatureImage) {
      newErrors.feature_image = "Featured image is required";
    }
    if (exploreType == "PROFESSIONAL" && !formData.company_name) {
      newErrors.company_name = "Organizer is required";
    }
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "About Event required";
    }
    if (!formData.event_category) {
      newErrors.event_category = "Event category is required";
    }

    if (formData.mode === "Offline" && !formData.event_location?.trim()) {
      newErrors.event_location = "Location is required for offline events";
    }

    if (!formData.start_time) {
      newErrors.start_time = "Start time is required";
    } else {
      const startDateTime = dayjs(formData.start_time);
      const now = dayjs();

      if (startDateTime.isBefore(now)) {
        newErrors.start_time = "Start time cannot be in the past";
      }
    }

    if (!formData.end_time) {
      newErrors.end_time = "End time is required";
    } else {
      const endDateTime = dayjs(formData.end_time);
      const now = dayjs();

      if (endDateTime.isBefore(now)) {
        newErrors.end_time = "End time cannot be in the past";
      } else if (formData.start_time) {
        const startDateTime = dayjs(formData.start_time);
        if (endDateTime.isBefore(startDateTime)) {
          newErrors.end_time = "End time must be after start time";
        }
      }
    }

    if (formData.is_paid) {
      if (!formData.price_amount) {
        newErrors.price_amount = "Price amount is required for paid events";
      } else if (parseFloat(formData.price_amount) <= 0) {
        newErrors.price_amount = "Price must be greater than 0";
      }
      if (!formData.price_currency) {
        newErrors.price_currency = "Currency is required for paid events";
      }
    }

    if (formData.event_access_level === "Private" && invitedUsers.length === 0) {
      newErrors.invitedUsers = "Please invite at least one user for private events";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      setIsSubmitting(true)
      const formPayload = new FormData();

      const payloadData = {
        ...formData,
        company_name:
          formData.company_name,
        start_time: formData.start_time
          ? dayjs(formData.start_time).toISOString()
          : null,
        end_time: formData.end_time
          ? dayjs(formData.end_time).toISOString()
          : null,
        feature_image: formData?.feature_image,
        media: undefined,
        ...(formData.mode === "Online"
          ? { event_link: formData.event_link, event_location: "" }
          : { event_location: formData.event_location, event_link: "" }),
      };

      Object.entries(payloadData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          if (key === "price_amount" && !formData.is_paid) return;
          formPayload.append(key, value);
        }
      });

      if (formData?.media?.length > 0) {
        formData?.media.forEach((file) => {
          formPayload.append("media", file);
        });
      }

      if (existingMedia) {
        for (const mediaObj of existingMedia) {
          const url = mediaObj.url || mediaObj;
          const response = await fetch(url);
          const blob = await response.blob();
          const filename = "existing-media.jpg";

          formPayload.append(
            "media",
            new File([blob], filename, { type: blob.type })
          );
        }
      }
      if (formData.event_access_level === "Private" && invitedUsers.length > 0) {
        for (const invitedUserId of invitedUsers) {
          formPayload.append("invited_user_ids", invitedUserId);
        }
      }

      if (onSubmit) {
        const params = { page: 0, my_events: false, type: exploreType }
        await onSubmit(formPayload);
        await dispatch(getSingleEvent(eventData?.id));
        await dispatch(getMyEvents({ page: 0, my_events: true, type: exploreType }));
        await dispatch(getAllEvents(params));
        openSnackbar("Event Updated successfully", "success", 4000);
      } else {
        const formObject = Object.fromEntries(formPayload.entries());
        const params = { page: 0, my_events: false }
        const res = await dispatch(postEvent(formPayload));
        console.log('hhhhhhhhhhhh')


        if (res?.type == postEvent.fulfilled.type) {
          await dispatch(getSingleEvent(eventData?.id));
          await dispatch(getMyEvents({ page: 0, my_events: true, type: exploreType }));
          await dispatch(getAllEvents(params));
          console.log("NNNNNNN", res)
          openSnackbar("Event Created successfully", "success", 4000);
          setFormData({
            title: "",
            description: "",
            event_type: exploreType,
            event_category: "",
            event_access_level: "Public",
            mode: "Offline",
            event_location: "",
            start_time: null,
            end_time: null,
            attendees_limit: "",
            is_paid: false,
            price_amount: "",
            price_currency: "",
            company_name: "",
            feature_image: null,
            media: [],
          })
          onClose();
          navigate("/my-feeds?tab=fourth");

        }
        else {
          // Handle error response from API
          const errorMessage = res?.payload || "Event creation failed";
          openSnackbar(errorMessage, "error", 4000);
          return;
        }

      }

    } catch (error) {
      console.error("Event creation failed:", error);
      openSnackbar("Event creation failed", error, 4000);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  // const handleLocationChange = async (input) => {
  //   try {
  //     if (!input || typeof input !== "string" || input.trim().length < 3 ) {
  //       setLocations([]);
  //       return;
  //     }

  //     if (input.length > 2) {
  //       const response = await getGoogleLocalPlaces(input);

  //       if (Array.isArray(response)) {
  //         setLocations(
  //           response.map((place) => ({
  //             description: place,
  //             placeId: place,
  //           }))
  //         );
  //       } else if (response?.predictions) {
  //         const validLocations = response.predictions
  //           .filter((place) => place?.description)
  //           .map((place) => ({
  //             description: place.description,
  //             placeId: place.place_id || place.description,
  //           }));
  //         setLocations(validLocations);
  //       } else {
  //         setLocations([]);
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error fetching locations:", error);
  //     setLocations([]);
  //   }
  // };


  const handleLocationChange = async (input) => {
    try {
      // Don't search if input is too short or undefined
      if (!input || typeof input !== "string" || input.trim().length < 3) {
        setLocations([]);
        return;
      }

      const response = await getGoogleLocalPlaces(input);

      if (Array.isArray(response)) {
        setLocations(
          response.map((place) => ({
            description: place,
            placeId: place,
          }))
        );
      } else if (response?.predictions) {
        const validLocations = response.predictions
          .filter((place) => place?.description)
          .map((place) => ({
            description: place.description,
            placeId: place.place_id || place.description,
          }));
        setLocations(validLocations);
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
    }
  };
  
  useEffect(() => {
    if (formData.event_access_level === "Private" && invitedUsers.length > 0) {
      const currentLimit = parseInt(formData.attendees_limit || 0, 10);

      if (currentLimit < invitedUsers.length) {
        setFormData(prev => ({
          ...prev,
          attendees_limit: invitedUsers.length.toString()
        }));
      }

      if (errors.attendees_limit) {
        setErrors(prev => ({ ...prev, attendees_limit: undefined }));
      }
    }
  }, [invitedUsers.length, formData.event_access_level]);

  const handleCloseClick = () => {
    setFormData({
      title: "",
      description: "",
      event_type: exploreType,
      event_category: "",
      event_access_level: "Public",
      mode: "Offline",
      event_location: "",
      start_time: null,
      end_time: null,
      attendees_limit: "",
      is_paid: false,
      price_amount: "",
      price_currency: "",
      company_name: "",
      feature_image: null,
      media: [],
    })
    onClose()
  }

  const handleAddIconClick = async () => {
    setSearchTerm("");
    setIsSearching(true);
    const params = {
      searchParam: "a",
      limit: 20,
      offsetValue: 1,
    }
    await dispatch(getGlobleUsers(params))
    setIsSearching(false);
    setShowInviteModal(true)
  }

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsSearching(true);
    debouncedSearch(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth >
      <DialogTitle>
        {editMode ? "Edit Event" : "Create New Event"}
        <IconButton
          style={{ position: "absolute", top: "10px", right: "10px" }}
          onClick={handleCloseClick}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* FEATURED IMAGE SECTION */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Featured Image *
            </Typography>
            {errors?.feature_image && (
              <Typography variant="caption" color="error">
                {errors?.feature_image}
              </Typography>
            )}

            <div
              style={{
                display: "flex",
                height: "250px",
                marginBottom: "20px",
                border: "2px dashed #ccc",
                borderRadius: "5px",
                padding: "10px",
                backgroundColor: "#f9f9f9",
                cursor: "pointer",
                position: "relative",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
              onClick={() => document.getElementById("feature-image-upload").click()}

              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = "#3f51b5";
                e.currentTarget.style.backgroundColor = "#e3f2fd";
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = "#ccc";
                e.currentTarget.style.backgroundColor = "#f9f9f9";
              }}
              onDrop={(e) => {
                e.preventDefault();
                const files = e.dataTransfer.files;
                if (files.length > 0 && files[0].type.startsWith("image/")) {
                  setFormData({ ...formData, feature_image: files[0] });
                } else {
                  setErrors(prev => ({
                    ...prev,
                    feature_image: "Only image files are allowed for featured image"
                  }));
                }
                e.currentTarget.style.borderColor = "#ccc";
                e.currentTarget.style.backgroundColor = "#f9f9f9";
              }}
            >
              {formData?.feature_image ? (
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <img
                    src={URL.createObjectURL(formData?.feature_image)}
                    alt="Featured Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "5px",

                    }}
                  />
                  <IconButton
                    size="small"
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      color: "white",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage("feature_image");
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ) : existingFeatureImage ? (
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <img
                    src={existingFeatureImage}
                    alt="Featured Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "5px",
                    }}
                  />
                  <IconButton
                    size="small"
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      color: "white",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExistingFeatureImage(null);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ) : (
                <div
                  onMouseEnter={() => setHover(true)}
                  onMouseLeave={() => setHover(false)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                    color: "#777",
                    borderRadius: 8,
                    cursor: "pointer",
                    transition: "box-shadow 0.3s ease, background 0.3s ease",
                    boxShadow: hover ? "0 4px 12px rgba(0, 0, 0, 0.2)" : "none",
                    background: hover
                      ? "linear-gradient(90deg, rgba(2,0,36,0.1) 0%, rgba(0,0,105,0.1) 0%, rgba(0,212,255,0.1) 100%)"
                      : "transparent",
                  }}
                >
                  <AddPhotoAlternateIcon fontSize="large" style={{ fontSize: 48, marginBottom: 16 }} />
                  <Typography variant="h6">Click to upload or drag & drop</Typography>
                </div>
              )}
            </div>

            <input
              accept="image/*"
              style={{ display: "none" }}
              id="feature-image-upload"
              type="file"
              onChange={handleFileChange("feature_image")}

            />
          </Grid>

          {/* EVENT GALLERY SECTION */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Media Gallery (Max 10 images)
            </Typography>

            <div
              className="upload-box"
              onClick={() => document.getElementById("gallery-upload").click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = "#3f51b5";
                e.currentTarget.style.backgroundColor = "#e3f2fd";
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = "#ccc";
                e.currentTarget.style.backgroundColor = "#f9f9f9";
              }}
              onDrop={(e) => {
                e.preventDefault();
                const files = Array.from(e.dataTransfer.files).filter(file =>
                  file.type.startsWith("image/") || file.type.startsWith("video/")
                );

                if (files.length > 0) {
                  const currentCount = (existingMedia?.length || 0) + (formData?.media?.length || 0);
                  const availableSlots = 10 - currentCount;
                  const filesToAdd = files.slice(0, availableSlots);

                  setFormData({
                    ...formData,
                    media: [...(formData?.media || []), ...filesToAdd]
                  });

                  filesToAdd.forEach(file => {
                    if (file.type.startsWith('video/')) {
                      generateVideoThumbnail(file);
                    }
                  });
                }

                e.currentTarget.style.borderColor = "#ccc";
                e.currentTarget.style.backgroundColor = "#f9f9f9";
              }}
            >
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                minHeight: "100px",
                alignItems: "center",

              }}>
                {existingMedia?.map((mediaObj, index) => {
                  const url = mediaObj.url || mediaObj;
                  const isVideoFile = isVideo(mediaObj);
                  const thumbnailUrl = videoThumbnails[url];

                  return (
                    <div key={`existing-${index}`} style={{
                      position: "relative",
                      width: "120px",
                      height: "120px",
                      flexShrink: 0,
                    }}>
                      {isVideoFile ? (
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                          {thumbnailUrl ? (
                            <>
                              <img
                                src={thumbnailUrl}
                                alt={`Video Preview ${index}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "5px",
                                  border: "1px solid #eee"
                                }}
                              />
                              <PlayCircleOutlineIcon
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  color: 'white',
                                  fontSize: 48,
                                  opacity: 0.8
                                }}
                              />
                            </>
                          ) : (
                            <div style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "5px",
                              border: "1px dashed #ccc"
                            }}>
                              <CircularProgress size={24} />
                            </div>
                          )}
                        </div>
                      ) : (
                        <img
                          src={url}
                          alt={`Gallery Preview ${index}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "5px",
                            border: "1px solid #eee"
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        style={{
                          position: "absolute",
                          top: "2px",
                          right: "2px",
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          color: "white",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExistingMedia(existingMedia.filter((_, i) => i !== index));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  );
                })}

                {formData?.media?.map((file, index) => {
                  const isVideoFile = isVideo(file);

                  return (
                    <div
                      key={`new-${index}`}
                      style={{
                        position: "relative",
                        width: "120px",
                        height: "120px",
                        flexShrink: 0,
                      }}
                    >
                      {isVideoFile ? (
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                          {videoThumbnails[file.name] ? (
                            <>
                              <img
                                src={videoThumbnails[file.name]}
                                alt={`Video Preview ${index}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "5px",
                                  border: "1px solid #eee"
                                }}
                              />
                              <PlayCircleOutlineIcon
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  color: 'white',
                                  fontSize: 48,
                                  opacity: 0.8
                                }}
                              />
                            </>
                          ) : (
                            <div style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "5px",
                              border: "1px dashed #ccc"
                            }}>
                              <CircularProgress size={24} />
                            </div>
                          )}
                        </div>
                      ) : (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Gallery Preview ${index}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "5px",
                            border: "1px solid #eee"
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        style={{
                          position: "absolute",
                          top: "2px",
                          right: "2px",
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          color: "white",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage("media", index);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  );
                })}

                {(existingMedia?.length + formData?.media?.length) >= 1 &&
                  (existingMedia?.length + formData?.media?.length) < 10 && (
                    <div
                      style={{
                        width: "120px",
                        height: "120px",
                        border: "1px dashed #ccc",
                        borderRadius: "5px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#666",
                        cursor: "pointer",
                        backgroundColor: "#f0f0f0",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById("gallery-upload").click();
                      }}
                    >
                      <AddPhotoAlternateIcon fontSize="large" style={{ fontSize: 48, marginBottom: 16 }} />
                      <Typography variant="caption" style={{ textAlign: "center", marginTop: 4 }}>
                        Add Images
                      </Typography>
                    </div>
                  )}

                {existingMedia?.length === 0 && formData?.media?.length === 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      height: "150px",
                      color: "#777",
                      gap: "6px"
                    }}
                  >
                    <BurstModeIcon style={{ fontSize: 48 }} />
                    <Typography variant="h6">Click to upload or drag & drop</Typography>
                    <Typography variant="body2">Images or videos</Typography>
                  </div>
                )}
              </div>

              <Typography variant="caption" style={{
                position: "absolute",
                bottom: "8px",
                right: "16px",
                color: "#666"
              }}>
                {existingMedia?.length + formData?.media?.length} / 10 images
              </Typography>
            </div>

            <input
              accept="image/*,video/*"
              style={{ display: "none" }}
              id="gallery-upload"
              type="file"
              multiple
              onChange={handleFileChange("media", true)}
            />
          </Grid>

          {/* Company Name */}
          {exploreType == "PROFESSIONAL" ? <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.company_name} required>
              <InputLabel>Organizer</InputLabel>
              <Select
                value={
                  formData.company_name ||
                  (editMode && eventData?.organizer?.name) ||
                  ""
                }
                onChange={(e) => {
                  setFormData({ ...formData, company_name: e.target.value });
                  setErrors(prev => ({ ...prev, company_name: undefined }));
                }}
                label="Organizer"


              >
                {/* <MenuItem value={userDetails?.name}>{userDetails?.name}</MenuItem> */}
                {verifiedCompanies?.map((company) => (
                  <MenuItem key={company} value={company}>
                    {company}
                  </MenuItem>
                ))}
                {editMode && eventData?.organizer?.name && !verifiedCompanies.includes(eventData?.organizer?.name) && (
                  <MenuItem value={eventData?.organizer?.name}>{eventData?.organizer?.name}</MenuItem>
                )}
              </Select>
              {errors.company_name && (
                <FormHelperText>{errors.company_name}</FormHelperText>
              )}
            </FormControl>
          </Grid> : ""}

          {/* Title */}
          <Grid item xs={12}>
            <TextField
              label="Title"
              required
              fullWidth
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                setErrors(prev => ({ ...prev, title: undefined }));
              }}
              error={!!errors.title}
              helperText={errors.title}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              label="About Event"
              fullWidth
              multiline
              required
              rows={4}
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                setErrors(prev => ({ ...prev, description: undefined }));
              }}
              error={!!errors.description}
              helperText={errors.description}
              inputProps={{
                style: {
                  whiteSpace: 'pre-wrap'
                }
              }}
              InputProps={{
                style: {
                  whiteSpace: 'pre-wrap'
                }
              }}
            />
          </Grid>

          {/* Event Category */}
          <Grid item xs={6}>
            <FormControl fullWidth error={!!errors.event_category} required>
              <InputLabel>Event Category</InputLabel>
              <Select
                value={formData.event_category}
                onChange={(e) => {
                  setFormData({ ...formData, event_category: e.target.value });
                  setErrors(prev => ({ ...prev, event_category: undefined }));
                }}
                label="Event Category"
                required
              >
                {eventCategories?.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
                {errors.event_category && (
                  <FormHelperText>{errors.event_category}</FormHelperText>
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Event Access Level */}
          <Grid item xs={6}>
            <FormControl fullWidth required>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={formData.event_access_level}
                onChange={handleAccessLevelChange}
              >
                <MenuItem value="Public">Public</MenuItem>
                <MenuItem value="Private">Private</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* NEW: Add People section for Private events */}
          {formData.event_access_level === "Private" && (
            <Grid item xs={12}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                border: '1px solid #e0e0e0'
              }}>
                <div>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                    Add People
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Invite specific people to your private event
                  </Typography>
                </div>

                <IconButton
                  color="primary"
                  onClick={() => handleAddIconClick()}

                  style={{ backgroundColor: '#e3f2fd' }}
                >
                  <PersonAddIcon />
                </IconButton>
              </div>

              {/* Display selected users */}
              {invitedUsers.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <Typography variant="body2" style={{ marginBottom: '8px' }}>
                    <strong>{invitedUsers.length}</strong> people invited
                  </Typography>
                  {/* Optional: Display list of invited user names */}
                  <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                    {globalusersData
                      .filter(user => invitedUsers.includes(user?.reference_id))
                      .map(user => (
                        <Typography key={user?.reference_id} variant="caption" display="block">
                          {user?.first_name} {user?.last_name}
                        </Typography>
                      ))}
                  </div>
                </div>
              )}
            </Grid>
          )}

          {/* Mode */}
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Mode</InputLabel>
              <Select
                value={formData.mode}
                onChange={handleModeChange}
              >
                <MenuItem value="Offline">Offline</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Conditional Location/Link */}
          {formData?.mode === "Online" ? (
            <Grid item xs={12}>
              <TextField
                label="Event Link"
                fullWidth
                value={formData?.event_link}
                onChange={(e) => {
                  setFormData({ ...formData, event_link: e.target.value });
                  setErrors(prev => ({ ...prev, event_link: undefined }));
                }}
                error={!!errors.event_link}
                helperText={errors.event_link}
                placeholder="https://example.com/event"
              />
            </Grid>
          ) : (
            // <Grid item xs={12}>
            //   <Autocomplete
            //     freeSolo
            //     options={locations}
            //     getOptionLabel={(option) =>
            //       typeof option === "string" ? option : option.description || ""
            //     }
            //     value={formData?.event_location || ""}
            //     onChange={(event, newValue) => {
            //       setFormData({
            //         ...formData,
            //         event_location:
            //           typeof newValue === "string"
            //             ? newValue
            //             : newValue?.description || "",
            //       });
            //       setErrors(prev => ({ ...prev, event_location: undefined }));
            //     }}
            //     onInputChange={(event, newValue) => {
            //       if (event?.type === "change") {
            //         handleLocationChange(newValue);
            //       }
            //     }}
            //     renderInput={(params) => (
            //       <TextField
            //         {...params}
            //         label="Event Location"
            //         fullWidth
            //         placeholder="Enter location"
            //         required
            //         error={!!errors.event_location}
            //         helperText={errors.event_location}
            //       />
            //     )}
            //     renderOption={(props, option) => (
            //       <li {...props}>
            //         <Typography variant="body2">
            //           {option?.description || option}
            //         </Typography>
            //       </li>
            //     )}
            //     filterOptions={(options, params) => options}
            //     noOptionsText={locations.length === 0 ? "Type to search locations" : "No locations found"}
            //     loading={locations?.length === 0}
            //     loadingText="Searching locations..."
            //   />
            // </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={locations}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  return option.description || "";
                }}
                value={formData?.event_location || ""}
                onChange={(event, newValue) => {
                  // Handle selection from dropdown
                  const locationValue = typeof newValue === "string"
                    ? newValue
                    : newValue?.description || "";
                  setFormData({
                    ...formData,
                    event_location: locationValue,
                  });
                  setErrors(prev => ({ ...prev, event_location: undefined }));
                }}
                onInputChange={(event, newValue, reason) => {
                  // Allow manual typing and update form data in real-time
                  if (reason === 'input') {
                    setFormData({
                      ...formData,
                      event_location: newValue,
                    });
                    setErrors(prev => ({ ...prev, event_location: undefined }));
                  }

                  // Search for locations when user types (with debounce)
                  if (event && event.type === 'change' && newValue && newValue.length > 2) {
                    handleLocationChange(newValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Event Location"
                    fullWidth
                    placeholder="Enter location (type manually or select from suggestions)"
                    required
                    error={!!errors.event_location}
                    helperText={errors.event_location || "You can type any location manually"}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Typography variant="body2">
                      {option?.description || option}
                    </Typography>
                  </li>
                )}
                filterOptions={(options, params) => {
                  // Return all options without filtering to allow both suggestions and manual input
                  return options;
                }}
                noOptionsText="Type to search locations or enter manually"
                loading={locations?.length === 0 && formData?.event_location?.length > 2}
                loadingText="Searching locations..."
                clearOnBlur={false}
                handleHomeEndKeys
                selectOnFocus
              />
            </Grid>
          )}

          {/* Date & Time Section */}
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                required
                label="Start Time"
                value={formData.start_time}
                onChange={(datetime) => {
                  setFormData({ ...formData, start_time: datetime });
                  setErrors(prev => ({
                    ...prev,
                    start_time: undefined,
                    end_time: datetime && formData.end_time &&
                      dayjs(formData.end_time).isBefore(datetime)
                      ? "End time must be after start time"
                      : prev.end_time
                  }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.start_time,
                    helperText: errors.start_time || "",
                    required: true
                  }
                }}
                minDateTime={dayjs()}
              />
            </LocalizationProvider>
          </Grid>

          {/* End Time */}
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="End Time"
                value={formData.end_time}
                required
                onChange={(datetime) => {
                  setFormData({ ...formData, end_time: datetime });
                  setErrors(prev => ({
                    ...prev,
                    end_time: undefined,
                    start_time: datetime && formData.start_time &&
                      dayjs(datetime).isBefore(formData.start_time)
                      ? "Start time must be before end time"
                      : prev.start_time
                  }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.end_time,
                    helperText: errors.end_time || "",
                    required: true
                  }
                }}
                minDateTime={formData.start_time || (dayjs())}
              />
            </LocalizationProvider>
          </Grid>

          {/* Attendees Limit */}
          <Grid item xs={6}>
            <TextField
              label="Maximum Attendees"
              fullWidth
              type="number"
              value={formData.attendees_limit}
              onChange={(e) => {
                const value = e.target.value;
                const numValue = parseInt(value, 10);

                if (formData.event_access_level === "Private" &&
                  !isNaN(numValue) &&
                  numValue < invitedUsers.length) {
                  return;
                }

                setFormData({ ...formData, attendees_limit: value });
              }}
              inputProps={{
                min: formData.event_access_level === "Private" ? invitedUsers.length : 0
              }}
              error={!!errors.attendees_limit}
              helperText={
                errors.attendees_limit ||
                (formData.event_access_level === "Private" && invitedUsers.length > 0
                  ? `${invitedUsers.length} users invited. Minimum attendees: ${invitedUsers.length}`
                  : '')
              }
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Pricing</InputLabel>
              <Select
                value="Free"
                disabled
                label="Pricing"
              >
                <MenuItem value="Free">Free</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Price Fields - Conditionally shown */}
          {formData.is_paid && (
            <>
              <Grid item xs={6}>
                <TextField
                  label="Price Amount"
                  fullWidth
                  type="number"
                  value={formData.price_amount}
                  onChange={(e) =>
                    setFormData({ ...formData, price_amount: e.target.value })
                  }
                  placeholder={
                    formData.price_currency
                      ? `${getSymbolFromCurrency(formData.price_currency) ||
                      formData.price_currency
                      } 0.00`
                      : "Enter amount"
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {getSymbolFromCurrency(formData.price_currency) || "$"}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.price_currency}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        price_currency: e.target.value,
                      })
                    }
                    label="Currency"
                  >
                    {currencyCodes.codes().map((code) => {
                      const currency = currencyCodes.code(code);
                      return (
                        <MenuItem key={code} value={code}>
                          {currency.code} - {currency.currency}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* Submit Button */}
          <Grid item xs={12} style={{ textAlign: "right", marginTop: 20 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : editMode ? (
                "Update Event"
              ) : (
                "Create Event"
              )}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Invite Modal with Search Bar */}
      <MuiDialog open={showInviteModal} onClose={() => setShowInviteModal(false)} maxWidth="sm" fullWidth>
        <MuiDialogTitle>
          Select Users to Invite
          <IconButton
            style={{ position: "absolute", top: "8px", right: "8px" }}
            onClick={() => setShowInviteModal(false)}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <MuiDialogContent>
          {/* Search Bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: isSearching && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
            style={{ marginBottom: "16px" }}
          />

          {/* Users List */}
          <List style={{ maxHeight: "400px", overflow: "auto" }}>
            {globalusersData?.length > 0 ? (
              globalusersData.map(user => (
                <ListItem
                  key={user?.reference_id}
                  dense
                  button
                  onClick={() => toggleInviteUser(user?.reference_id)}
                >
                  <Checkbox
                    edge="start"
                    checked={invitedUsers.includes(user?.reference_id)}
                    tabIndex={-1}
                    disableRipple
                    onClick={e => e.stopPropagation()}
                  />
                  <Avatar src={user?.profile_url} style={{ margin: 8 }} />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2">
                      {user?.first_name} {user?.last_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {user?.email}
                    </Typography>
                  </div>
                </ListItem>
              ))
            ) : (
              <ListItem>
                <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", width: "100%" }}>
                  No users found
                </Typography>
              </ListItem>
            )}
          </List>
        </MuiDialogContent>
        <DialogActions>
          <MuiButton onClick={() => setShowInviteModal(false)} color="primary" variant="contained">
            Done ({invitedUsers.length} selected)
          </MuiButton>
        </DialogActions>
      </MuiDialog>
    </Dialog>
  );
};

export default CreateEventModal;